import React, { FC } from "react";
import Skeleton from "react-loading-skeleton";
import { FileField } from "@pd/pd-ui-kit";
import type { FileFieldFile } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import IconTrash from "~/components/Icons/icon-trash";
import { uploadFile } from "~/libs/uploadFile";
import {
  useDeleteBonusQFileAppeal,
  usePostBonusQFileAppeal,
  usePutBonusQFileAppeal,
} from "~/pages/BonusQReport/hooks/useGetBonusQFileAppeal";
import { usePermissions } from "~/shared/context/PermissionContext";

import s from "./ReportDocumentControl.less";

interface IProps {
  id?: string;
  label?: string;
  link?: string;
  isLoading?: boolean;
}

// accept у FileField — только MIME-типы (расширения вида .pdf не работают).
const ACCEPT_TYPES = ["application/pdf", "image/*"];

const ReportDocumentControl: FC<IProps> = ({ id, label, link, isLoading }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { mutate: addFile } = usePostBonusQFileAppeal();
  const { mutate: updateFile } = usePutBonusQFileAppeal();
  const { mutate: removeFile } = useDeleteBonusQFileAppeal();

  const fileValue: FileFieldFile | null = link
    ? { id: String(id ?? ""), name: label ?? "", link }
    : null;

  const handleUploadFile = async (file: File) => {
    const uploaded = await uploadFile(file, () => {
      alert(t("common.data_uploaded_error"));
    });

    if (uploaded?.id) {
      // Файл один глобальный: если файл уже есть — замена (PUT),
      // иначе создание (POST). Мутации сами инвалидируют getBonusQFileAppeal.
      if (fileValue) {
        updateFile(Number(uploaded.id));
      } else {
        addFile(Number(uploaded.id));
      }
    }
  };

  const handleDeleteFile = () => {
    if (id) {
      removeFile();
    }
  };

  if (isLoading) {
    return (
      <div className={s.wrapper}>
        <div className={s.fileControlsLoader}>
          <Skeleton />
        </div>
      </div>
    );
  }

  return (
    <div className={s.fileControl}>
      {fileValue ? (
        <>
          {/* Отображение файла и скачивание (по клику открывается link). */}
          <FileField value={fileValue} />
          <div className={s.fileControls}>
            {hasPermission(
              "employeeAccounting.statistics.quarter.appeal.editAppealFile"
            ) && (
              // Замена файла: у FileField в document-варианте нет своей
              // кнопки замены, поэтому рядом — пикер в режиме «заменить».
              <FileField
                value={null}
                accept={ACCEPT_TYPES}
                uploadLabel={t("ui.replace_file")}
                onUpload={handleUploadFile}
              />
            )}
            {hasPermission(
              "employeeAccounting.statistics.quarter.appeal.deleteAppealFile"
            ) && (
              <button
                type="button"
                onClick={handleDeleteFile}
                className={s.customBtn}
              >
                <IconTrash fill="#878E94" />
              </button>
            )}
          </div>
        </>
      ) : hasPermission(
          "employeeAccounting.statistics.quarter.appeal.createAppealFile"
        ) ? (
        <FileField
          label={label}
          value={null}
          accept={ACCEPT_TYPES}
          uploadLabel={t("ui.upload_file")}
          onUpload={handleUploadFile}
        />
      ) : (
        <div className={s.filePreviewNone}>{label}</div>
      )}
    </div>
  );
};

export default ReportDocumentControl;
